import { Dialog, DialogTitle, Grid, Stack, Typography, styled } from "@mui/material";
import { AuditRecordModel } from "../../gql-types.generated";
import DialogCloseButton from "./DialogCloseButton";
import { PaddedDialogContent } from "../../util/SharedStyles";
import { capitalizeWithUnderscoreRemoval } from '../../util/Common';
import JsonViewer from '../JsonViewer';
import { DateTime } from "luxon";
import { getFormattedDateTimeString } from "../../util/DateTimeUtility";

const TitleBar = styled(DialogTitle)((props) => ({
    height: '56px',
    padding: '4px 24px 0px 24px !important'
}));

const TitleGrid = styled(Grid)((props) => ({
    height: '100%',
}));

const OldValuesJsonViewer = styled(JsonViewer)(({ theme }) => ({
    '& .json-string': {
        color: theme.palette.text.secondary,
    },
    '& .json-number': {
        color: theme.palette.text.secondary,
    },
    '& .json-boolean': {
        color: theme.palette.text.secondary,
    }
}));

const NewValuesJsonViewer = styled(JsonViewer)(({ theme }) => ({
    '& .json-number': {
        color: theme.palette.success.main,
    },
    '& .json-boolean': {
        color: theme.palette.success.main,
    },
    '& .json-null': {
        color: theme.palette.success.main,
    },
    '& .json-undefined': {
        color: theme.palette.success.main,
    }
}));

interface DialogProps {
    isOpen: boolean;
    auditRecord?: AuditRecordModel;
    onClose: () => void;
    error?: Error | undefined;
}

const AuditRecordDetailsDialog: React.FC<DialogProps> = props => {
    const {isOpen, auditRecord, onClose} = props;
    let header = "Change Log Details";

    const getDisplayContent = () => {
        if (auditRecord) {
            return getDisplayFields();
        }
        return null;
    };

    const getDisplayFields = () => {
        return(
            <Stack rowGap={2}>
                <Grid container item xs={12} spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant='caption' aria-label='entity-name'>Entity Name</Typography>
                        <Typography variant='body1'>{auditRecord?.entityName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant='caption' aria-label='entity-state'>Entity State</Typography>
                        <Typography variant='body1'>{capitalizeWithUnderscoreRemoval(auditRecord?.entityState ?? '', " ")}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant='caption' aria-label='created-time'>Change Date</Typography>
                        <Typography variant='body1'>{getFormattedDateTimeString(auditRecord?.createdTime, { format: DateTime.DATETIME_FULL })}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant='caption' aria-label='user-name'>Changed By</Typography>
                        <Typography variant='body1'>{auditRecord?.userName}</Typography>
                    </Grid>
                </Grid>
                <Grid container item xs={12} spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant='caption' aria-label='old-values'>Previous Values</Typography>
                        <OldValuesJsonViewer value={auditRecord?.oldValues as string} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant='caption' aria-label='new-values'>New Values</Typography>
                        <NewValuesJsonViewer value={auditRecord?.newValues as string}/>
                    </Grid>
                </Grid>
            </Stack>
        );
    };

    return <Dialog
        aria-label="audit-record-details-dialog"
        maxWidth='md'
        open={isOpen}
        scroll="paper"
        fullWidth
    >
        <TitleBar id='dialog-title'>
            <TitleGrid container item direction="row" justifyContent="space-between" alignItems="center" xs={11}>
                <Typography variant="body1">{header}</Typography>
            </TitleGrid>
            <DialogCloseButton onClick={onClose}></DialogCloseButton>
        </TitleBar>
        <PaddedDialogContent dividers padding="10px 30px 30px 30px">
            {getDisplayContent()}
        </PaddedDialogContent>
    </Dialog>
}

export default AuditRecordDetailsDialog;
